import { Description, FieldError, Input, TextField } from "@heroui/react";
import { Icon } from "@iconify/react";
import { memo, useState } from "react";
import { PresetPill } from "@funnel-ui/primitives/PresetPill";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

type TextShortProps = {
  question: string;
  description?: string;
  placeholder?: string;
  initialValue?: string;
  presets?: string[];
  randomize?: () => string;
  maxLength?: number;
  validate?: (value: string) => string | undefined;
  onChange?: (value: string) => void;
  onValidityChange?: (valid: boolean) => void;
  className?: string;
  testId?: string;
};

const TextShortImpl = ({
  question,
  description,
  placeholder = "Type here...",
  initialValue = "",
  presets,
  randomize,
  maxLength = 60,
  validate,
  onChange,
  onValidityChange,
  className,
  testId,
}: TextShortProps) => {
  const [value, setValue] = useState(initialValue);
  const error = validate ? validate(value) : undefined;
  const valid = value.trim().length > 0 && !error;

  const handleChange = (next: string): void => {
    setValue(next);
    onChange?.(next);
    onValidityChange?.(next.trim().length > 0 && !validate?.(next));
  };

  return (
    <section data-testid={testId} className={`flex flex-col gap-5 ${className ?? ""}`}>
      <StepHeading title={question} description={description} editablePath="question" />

      {presets && presets.length > 0 && (
        <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1">
          {presets.map((preset) => (
            <PresetPill
              key={preset}
              label={preset}
              selected={value === preset}
              onPress={() => handleChange(preset)}
            />
          ))}
          {randomize && (
            <PresetPill
              label="Random"
              leading={<Icon icon="gravity-ui:wand" width={14} height={14} />}
              onPress={() => handleChange(randomize())}
            />
          )}
        </div>
      )}

      <TextField value={value} onChange={handleChange} isInvalid={!!error}>
        <Input placeholder={placeholder} maxLength={maxLength} autoComplete="off" />
        {error ? (
          <FieldError>{error}</FieldError>
        ) : description ? (
          <Description className="sr-only">{description}</Description>
        ) : null}
      </TextField>

      <p role="status" aria-live="polite" className="sr-only">
        {valid ? "Input valid" : (error ?? "Input required")}
      </p>
    </section>
  );
};

export const TextShort = memo(TextShortImpl);

export default TextShort;
